<template>
  <div class="device-activation">
    <el-card class="activation-card">
      <div slot="header" class="card-header">
        <div>
          <h2>售后设备激活</h2>
          <p>查询 SN 的阿里激活状态，并按需执行预激活或正式激活。</p>
        </div>
        <el-tag v-if="summary" :type="summary.formalActivated ? 'success' : 'warning'">
          {{ summary.formalActivated ? '已正式激活' : '未正式激活' }}
        </el-tag>
      </div>

      <el-form label-position="top" @submit.native.prevent>
        <el-form-item label="设备 SN">
          <div class="query-row">
            <el-input
              v-model.trim="sn"
              placeholder="请输入设备 SN，例如 VC20203261101096"
              clearable
              @keyup.enter.native="queryInfo">
            </el-input>
            <el-button
              type="primary"
              icon="el-icon-search"
              :loading="loading"
              :disabled="!sn"
              @click="queryInfo">查询</el-button>
          </div>
        </el-form-item>
      </el-form>

      <el-alert
        v-if="errorMsg"
        class="result-alert"
        :title="errorMsg"
        type="error"
        show-icon
        :closable="false">
      </el-alert>

      <div v-if="info" class="result-panel">
        <div class="summary-grid">
          <div class="summary-item">
            <span>SN</span>
            <strong>{{ info.sn }}</strong>
          </div>
          <div class="summary-item">
            <span>接口状态</span>
            <strong>{{ info.code || '-' }}</strong>
          </div>
          <div class="summary-item">
            <span>业务状态</span>
            <strong>{{ joined(summary.bizStatuses) }}</strong>
          </div>
          <div class="summary-item">
            <span>激活状态</span>
            <strong>{{ joined(summary.statuses) }}</strong>
          </div>
        </div>

        <div v-if="latestActivation" class="activation-result">
          <span>最近一次激活结果</span>
          <strong>{{ latestActivation.msg || '-' }}</strong>
          <span>{{ latestActivation.data || '' }}</span>
        </div>

        <div v-if="summary.canActivate" class="action-row">
          <el-button
            type="warning"
            icon="el-icon-time"
            :loading="activatingType === 'informal'"
            :disabled="isActivating"
            @click="confirmActivate('informal')">预激活</el-button>
          <el-button
            type="danger"
            icon="el-icon-finished"
            :loading="activatingType === 'formal'"
            :disabled="isActivating"
            @click="confirmActivate('formal')">正式激活</el-button>
        </div>

        <el-table
          class="sku-table"
          :data="info.items"
          border
          empty-text="没有查询到 SKU 信息">
          <el-table-column prop="skuCode" label="SKU" min-width="220"></el-table-column>
          <el-table-column prop="status" label="状态" width="150"></el-table-column>
          <el-table-column prop="bizStatus" label="业务状态" width="130"></el-table-column>
          <el-table-column prop="informalActivatedCount" label="预激活次数" width="120"></el-table-column>
          <el-table-column prop="informalActivateQuota" label="预激活额度" width="120"></el-table-column>
          <el-table-column prop="informalActivatedTime" label="预激活时间" min-width="170"></el-table-column>
          <el-table-column prop="informalExpiredTime" label="预激活过期" min-width="170"></el-table-column>
          <el-table-column prop="formalActivatedTime" label="正式激活时间" min-width="170"></el-table-column>
          <el-table-column prop="formalExpiredTime" label="正式激活过期" min-width="170"></el-table-column>
        </el-table>
      </div>
    </el-card>

    <el-dialog title="登录" :visible.sync="loginVisible" :width="dialogWidth" center>
      <el-form :model="loginForm" label-width="80px" @submit.native.prevent>
        <el-form-item label="用户名">
          <el-input v-model="loginForm.username" placeholder="请输入用户名"></el-input>
        </el-form-item>
        <el-form-item label="密码">
          <el-input type="password" v-model="loginForm.password" placeholder="请输入密码" @keyup.enter.native="handleLogin"></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="loginVisible = false">取消</el-button>
        <el-button type="primary" :disabled="!isLoginValid" @click="handleLogin">确定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import login from 'network/login'
import { activateDevice, queryDeviceActivation } from 'network/deviceActivation'

export default {
  name: 'DeviceActivation',
  data() {
    return {
      sn: '',
      loading: false,
      activatingType: '',
      info: null,
      latestActivation: null,
      errorMsg: '',
      loginVisible: false,
      loginForm: {
        username: '',
        password: ''
      }
    }
  },
  computed: {
    summary() {
      return this.info ? this.info.summary : null
    },
    isActivating() {
      return this.activatingType !== ''
    },
    isLoginValid() {
      return this.loginForm.username.trim() !== '' && this.loginForm.password.trim() !== ''
    },
    dialogWidth() {
      return window.innerWidth <= 600 ? '90%' : '30%'
    }
  },
  methods: {
    async queryInfo() {
      const sn = this.sn.trim()
      if (!sn) {
        this.$message.warning('请输入设备 SN')
        return
      }

      this.loading = true
      this.errorMsg = ''
      this.latestActivation = null
      try {
        const result = await queryDeviceActivation(sn)
        if (this.handleAuthResult(result)) return
        if (result.code === 0 && result.data) {
          this.info = result.data
          this.$message.success('查询成功')
        } else {
          this.info = null
          this.errorMsg = result.msg || '查询失败'
        }
      } catch (err) {
        console.error(err)
        this.errorMsg = this.resolveErrorMessage(err, '查询失败')
      } finally {
        this.loading = false
      }
    },
    confirmActivate(activateType) {
      const actionText = activateType === 'formal' ? '正式激活' : '预激活'
      this.$confirm(`确认对 ${this.sn} 执行${actionText}？`, '确认操作', {
        confirmButtonText: '确认',
        cancelButtonText: '取消',
        type: activateType === 'formal' ? 'warning' : 'info'
      }).then(() => {
        this.doActivate(activateType)
      }).catch(() => {})
    },
    async doActivate(activateType) {
      const sn = this.sn.trim()
      if (!sn) {
        this.$message.warning('请输入设备 SN')
        return
      }

      this.activatingType = activateType
      this.errorMsg = ''
      try {
        const result = await activateDevice(sn, activateType)
        if (this.handleAuthResult(result)) return
        if (result.code === 0 && result.data && result.data.info) {
          this.latestActivation = result.data.activationResult
          this.info = result.data.info
          this.$message.success('激活成功，已刷新设备信息')
        } else {
          this.latestActivation = result.data ? result.data.activationResult : null
          this.errorMsg = result.msg || '激活失败'
        }
      } catch (err) {
        console.error(err)
        this.errorMsg = this.resolveErrorMessage(err, '激活失败')
      } finally {
        this.activatingType = ''
      }
    },
    handleAuthResult(result) {
      if (result && result.error_code === 901) {
        this.loginVisible = true
        this.errorMsg = result.error || '请先登录'
        return true
      }
      return false
    },
    resolveErrorMessage(err, prefix) {
      if (err.response && err.response.data) {
        const data = err.response.data
        if (typeof data === 'string') return `${prefix}：${data}`
        if (data.error_code === 901) {
          this.loginVisible = true
          return data.error || '请先登录'
        }
        if (data.msg) return `${prefix}：${data.msg}`
        if (data.error) return `${prefix}：${data.error}`
      }
      if (err.message) return `${prefix}：${err.message}`
      return `${prefix}，请检查网络后重试`
    },
    async handleLogin() {
      if (!this.isLoginValid) return
      try {
        const result = await login(this.loginForm)
        if (result.token) {
          localStorage.setItem('token', result.token)
          this.loginVisible = false
          this.$message.success('登录成功')
          if (this.sn) this.queryInfo()
        } else {
          this.$message.warning('登录失败')
        }
      } catch (err) {
        const errorMsg = err.response && err.response.data && err.response.data.error ? err.response.data.error : '登录失败'
        this.$message.warning(errorMsg)
      }
    },
    joined(list) {
      return list && list.length ? list.join(' / ') : '-'
    }
  }
}
</script>

<style scoped>
.device-activation {
  min-height: calc(100vh - 60px);
  padding: 36px 24px;
  background: #f5f7fb;
}
.activation-card {
  width: 100%;
  max-width: 1180px;
  margin: 0 auto;
  border-radius: 8px;
}
.card-header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
}
.card-header h2 {
  margin: 0;
  color: #17233d;
  font-size: 24px;
  font-weight: 600;
}
.card-header p {
  margin: 8px 0 0;
  color: #7a8494;
  font-size: 14px;
}
.query-row {
  display: flex;
  gap: 12px;
}
.query-row .el-button {
  flex: 0 0 112px;
}
.result-alert {
  margin-top: 18px;
}
.result-panel {
  margin-top: 22px;
}
.summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}
.summary-item {
  padding: 14px 16px;
  background: #fbfcff;
  border: 1px solid #e4e8f1;
  border-radius: 8px;
}
.summary-item span {
  display: block;
  color: #7a8494;
  font-size: 13px;
}
.summary-item strong {
  display: block;
  margin-top: 8px;
  color: #27364a;
  font-size: 15px;
  overflow-wrap: anywhere;
}
.activation-result {
  display: flex;
  gap: 12px;
  align-items: center;
  margin-top: 16px;
  padding: 12px 16px;
  color: #2b6a2f;
  background: #f0f9eb;
  border: 1px solid #d9ecff;
  border-radius: 8px;
}
.activation-result span:first-child {
  color: #60836a;
}
.action-row {
  margin-top: 18px;
}
.sku-table {
  margin-top: 18px;
}
@media (max-width: 900px) {
  .summary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
@media (max-width: 650px) {
  .device-activation {
    padding: 24px 12px;
  }
  .card-header {
    display: block;
  }
  .card-header .el-tag {
    margin-top: 12px;
  }
  .query-row {
    display: block;
  }
  .query-row .el-button {
    width: 100%;
    margin-top: 12px;
  }
  .summary-grid {
    grid-template-columns: 1fr;
  }
  .action-row .el-button {
    width: 100%;
    margin: 8px 0 0;
  }
}
</style>
